/**
 * Jobs section - Refactored with modular architecture
 *
 * Phase 1 refactoring complete:
 * - Cell components extracted to components/cells/
 * - Column configuration extracted to config/
 * - Hook logic split into 4 focused hooks
 * - Type definitions centralized in types/
 * - Reduced from 507 lines to ~260 lines
 */

import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { AppRouteBody } from '@/components/AppRouteBody';
import { Tabs } from '@/components/chariot-ui';
import { FilterDropdown } from '@/components/filters/FilterDropdown';
import { ClearFiltersButton } from '@/components/legacy-ui/ClearFiltersButton';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { Table } from '@/components/table/Table';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useCancelJob, useReRunJob } from '@/hooks/useJobs';
import { useNextRun } from '@/hooks/useNextRun';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { JobsRunningOverTime } from '@/sections/insights/metrics/widgets/jobs/JobsRunningOverTime';
import { Job, JobStatus, JobStatusLabel } from '@/types';
import { useStorage } from '@/utils/storage/useStorage.util';

import { JobsActions } from './components/actions';
import { getStatusIcon } from './components/cells';
import { JobDetailPanel } from './components/JobDetailPanel';
import { getJobActionOptions } from './config/actionOptions';
import { createJobColumns } from './config/columnDefinitions';
import {
  useJobsActionHandlers,
  useJobsCoordinator,
  useJobsKeyboard,
} from './hooks';
import { JobFilters } from './types';
import { downloadFile } from './utils/downloadHelpers.util';

/**
 * Default filter state for jobs
 */
const defaultFilters: JobFilters = {
  query: [],
  search: '',
  sort: {
    orderBy: 'updated',
    descending: true, // Newest first by default
  },
};

/**
 * Jobs component - Main orchestration for jobs table and graph views
 *
 * Responsibilities:
 * - Manage filter state
 * - Coordinate data fetching via useJobsCoordinator
 * - Handle action callbacks (rerun, cancel, download)
 * - Render table or graph view based on URL search params
 */
const Jobs = () => {
  const { canWriteJobs } = useEntitlements();

  // Tab view state from URL search params (follows Insights pattern)
  const [searchParams, setSearchParams] = useSearchParams();
  const currentView = searchParams.get('view') || 'table'; // Default to table

  const handleTabChange = (tab: { value: string }) => {
    setSearchParams({ view: tab.value });
  };

  // Filter state management with localStorage persistence
  const [filters, setFilters] = useStorage(
    { queryKey: 'jobsFilter' },
    defaultFilters
  );

  const { nextRunText } = useNextRun();

  // Coordinator hook orchestrates query strategy (default/search/risk)
  const {
    data: jobs,
    error: jobsError,
    status: jobsStatus,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    hasNextPage,
    refetch,
    jobStats,
  } = useJobsCoordinator(filters);

  // Scan level for future use
  // const { isFrozen } = useScanLevel();

  // Row selection state for bulk actions
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // Selected job for detail panel (click to expand/collapse)
  const [selectedJobKey, setSelectedJobKey] = useState<string | null>(null);

  // Wrap setFilters to clear the detail panel selection whenever filters change
  const updateFilters: typeof setFilters = useCallback(
    (...args) => {
      setSelectedJobKey(null);
      setFilters(...args);
    },
    [setFilters]
  );

  // Find the selected job object from the jobs array
  const selectedJob = useMemo(
    () =>
      selectedJobKey
        ? jobs.find((job: Job) => job.key === selectedJobKey)
        : null,
    [selectedJobKey, jobs]
  );

  // Compute selected jobs from selected row keys
  const selectedJobs = useMemo(() => {
    return selectedRows
      .map(key => jobs.find((job: Job) => job.key === key))
      .filter((job): job is NonNullable<typeof job> => job !== undefined);
  }, [selectedRows, jobs]);

  // Calculate isFilteredEmpty: true when status is success but data is empty
  // This indicates filters returned no results (not a loading/error state)
  const isFilteredEmpty = jobsStatus === 'success' && jobs.length === 0;

  // Force table remount on empty → data transition to fix rendering bug
  // where clearing a search with no results leaves table stuck showing "No Jobs Found"
  const tableKey = useTableRemountOnTransition(isFilteredEmpty, jobs.length);

  // Action mutations
  const { mutateAsync: reRunJob } = useReRunJob();
  const { mutateAsync: cancelJob } = useCancelJob();

  // Download handler for job artifacts using extracted utility
  const handleDownload = useCallback(
    (s3DownloadURL: string, fileName: string) => {
      downloadFile(s3DownloadURL, fileName);
    },
    []
  );

  // Action handlers for row actions
  const actionHandlers = useMemo(
    () => ({
      handleRerun: (jobKey: string) => reRunJob({ jobKey }),
      handleCancel: (jobKey: string) => cancelJob({ jobKey }),
      handleDownload,
    }),
    [reRunJob, cancelJob, handleDownload]
  );

  // Bulk action handlers for keyboard shortcuts
  const bulkHandlers = useJobsActionHandlers({
    selectedJobs,
    refetch,
  });

  // Keyboard shortcut handlers
  const { handleKeyboardAction } = useJobsKeyboard({
    selectedJobs,
    handlers: bulkHandlers,
  });

  // Create columns using extracted configuration
  const columns = useMemo(() => createJobColumns(), []);

  // Status filter options with counts and icons
  const statusFilters = useMemo(() => {
    // Filter out async statuses and Partial from the filter options
    return Object.entries(JobStatusLabel)
      .filter(
        ([value]) =>
          value !== JobStatus.AsyncRunning &&
          value !== JobStatus.AsyncReturning &&
          value !== JobStatus.Partial
      )
      .map(([value, label]) => ({
        id: `status-${value}`,
        label,
        value: `status:${value}`,
        count: (jobStats[value as JobStatus] || 0) as number,
        icon: (
          <div>
            <div className="pr-3">
              {getStatusIcon(value as unknown as JobStatus, false)}
            </div>
          </div>
        ),
      }));
  }, [jobStats]);

  // Set default view param if not present
  useEffect(() => {
    if (!searchParams.has('view')) {
      setSearchParams({ view: 'table' }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Tab options
  const jobTabs = [
    { label: 'Jobs Table', value: 'table' },
    { label: 'Jobs Graph', value: 'graph' },
  ];

  // Find selected tab object
  const selectedTab =
    jobTabs.find(tab => tab.value === currentView) || jobTabs[0];

  return (
    <AppRouteBody title="Jobs" data-tutorial="jobs-section">
      <main className="flex size-full flex-col gap-6">
        {/* Tabs navigation */}
        <Tabs
          tabs={jobTabs}
          selected={selectedTab}
          onSelect={handleTabChange}
        />

        {/* Conditional rendering based on tab */}
        {currentView === 'table' ? (
          <>
            {/* TableControlsContainer - Unified filters and selection bar */}
            <TableControlsContainer
              data-testid="jobs-controls"
              filterLabel={true}
              filters={
                <>
                  <div data-tutorial="jobs-filter-status">
                    <FilterDropdown
                      id="status"
                      label="Status"
                      options={statusFilters}
                      selectedValues={filters.query}
                      maxSelections={1}
                      onApply={selected => {
                        // DynamoDB limitation: Only single status filter supported
                        // maxSelections={1} enforces single-select at UI level
                        updateFilters(prev => ({
                          ...prev,
                          query: selected.map(opt => opt.value as string),
                          search: '',
                        }));
                      }}
                      onClear={() => {
                        // Preserve sort when clearing filters - spread operator maintains all properties
                        updateFilters(prev => ({
                          ...prev,
                          query: [],
                          search: '',
                        }));
                      }}
                      width={300}
                    />
                  </div>
                  {filters.query.length > 0 && (
                    <ClearFiltersButton
                      onClick={() => updateFilters(defaultFilters)}
                      label="Clear"
                    />
                  )}
                </>
              }
              filtersRight={
                <div data-tutorial="jobs-search">
                  <SearchBar
                    size="sm"
                    className="h-fit w-64 shrink-0"
                    placeholder="Search jobs"
                    value={filters.search}
                    onChange={value => {
                      // Preserve sort when updating search - spread operator maintains all properties
                      updateFilters(prev => ({
                        ...prev,
                        search: value,
                        query: [],
                      }));
                    }}
                  />
                </div>
              }
              selectionBar={
                canWriteJobs ? (
                  <TableSelectionBar
                    embedded={true}
                    alwaysVisible={true}
                    isVisible={selectedRows.length > 0}
                    selectedCount={selectedJobs.length}
                    totalCount={jobs.length}
                    hasNextPage={hasNextPage}
                    resourceLabel="jobs"
                    emptySelectionMessage="Select jobs to perform bulk actions"
                    actions={
                      <JobsActions
                        jobs={selectedJobs}
                        selectedRowsCount={selectedRows.length}
                        onClearSelection={() => setSelectedRows([])}
                        refetch={refetch}
                        disabled={selectedJobs.length === 0}
                        nextRunText={nextRunText}
                      />
                    }
                  />
                ) : undefined
              }
            />

            {/* Job detail panel - shown when a row is clicked */}
            {selectedJob && (
              <JobDetailPanel
                job={selectedJob}
                onClose={() => setSelectedJobKey(null)}
              />
            )}

            <Table
              key={tableKey}
              name="jobs"
              columns={columns}
              data={jobs}
              onRowClick={(job: Job) => {
                // Toggle: click same row closes panel, click different row opens it
                setSelectedJobKey(prev => (prev === job.key ? null : job.key));
              }}
              error={jobsError}
              status={jobsStatus}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              isFetching={isFetching}
              hasNextPage={hasNextPage}
              sort={{
                orderBy: filters.sort?.orderBy || 'updated',
                descending: filters.sort?.descending ?? true,
                onSort: (newSort: {
                  orderBy: keyof Job;
                  descending: boolean;
                }) => {
                  updateFilters(prev => ({
                    ...prev,
                    sort: {
                      orderBy: newSort.orderBy,
                      descending: newSort.descending,
                    },
                  }));
                },
              }}
              noData={{
                icon: (
                  <InformationCircleIcon className="text-chariot-stroke size-16" />
                ),
                title: 'No Jobs Found',
                description: 'No jobs match your current filters.',
              }}
              selection={
                canWriteJobs
                  ? {
                      value: selectedRows,
                      onChange: setSelectedRows,
                      selectAll: true,
                    }
                  : undefined
              }
              keyboardActions={{
                codes: ['KeyR'],
                onKeyboardAction: (code, jobs) => {
                  if (!canWriteJobs) return;
                  handleKeyboardAction(code, jobs);
                },
              }}
              rowActions={job => ({
                menu: {
                  items: canWriteJobs
                    ? getJobActionOptions({
                        job,
                        handlers: actionHandlers,
                      })
                    : [],
                },
              })}
            />
          </>
        ) : (
          <>
            {/* Graph view */}
            <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border">
              <div className="border-chariot-stroke border-b px-4 py-3">
                <h3 className="text-chariot-text-primary text-sm font-semibold">
                  Average Running Jobs Over Time
                </h3>
                <p className="text-chariot-text-secondary text-xs">
                  5-minute intervals for the past 24 hours
                </p>
              </div>
              <div className="h-96 p-4">
                <JobsRunningOverTime />
              </div>
            </div>
          </>
        )}
      </main>
    </AppRouteBody>
  );
};

export default Jobs;
